import moment from 'moment';
import { mapResourceName } from '@/shared/services/ObjectDisplayService';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import handleHttpError from '@/shared/services/HandleHttpError';
import api from '@/shared/api';
import config from '@/config';
import adminpulse from '@/shared/utils/adminpulse';
import AuthService, { privileges, functionalities } from '@/shared/services/AuthService';
import guid from '../../utils/guid';
/*
 * Helper for retrieving and translating resources
 */
const handleResponse = intl => ({ data }) =>
  data
    .map(item => {
      const translatedName = mapResourceName(item, intl);

      return {
        ...item,
        key: item.id,
        code: item.code || '',
        label: translatedName,
      };
    })
    .sort(dynamicSortMultiple('code', 'label'));

export const retrieveResources = (intl, type = -1) =>
  api.base
    .get(`${config.appUrls.api}/api/Resource/Registration?type=${type}`)
    .then(handleResponse(intl), handleHttpError(intl));

export const retrieveWorkResources = intl => retrieveResources(intl, 0);

export const retrieveAllResources = intl => retrieveResources(intl, -1);

export const getInForfait = (invoiceStreamId, resourceId) => {
  if (!invoiceStreamId || invoiceStreamId === guid.empty || !resourceId)
    return Promise.resolve(null);
  return api.base
    .get(
      `${config.appUrls.api}/api/resource/InForfait?invoiceStream=${invoiceStreamId}&resource=${resourceId}`,
    )
    .then(({ data }) => {
      if (data === null || typeof data === 'undefined' || data === '') return null;
      return data;
    });
};
export const getPrice = (invoiceStreamId, categoryId, userId = null) => {
  if (!invoiceStreamId || invoiceStreamId === guid.empty || !categoryId)
    return Promise.resolve({ data: null });
  return api.base.get(
    `${config.appUrls.api}/api/price/?invoiceStream=${invoiceStreamId}&category=${categoryId}&user=${userId}`,
  );
};
export const getInForfaitAndPrice = (invoiceStreamId, resource, userId) =>
  Promise.all([
    getInForfait(invoiceStreamId, resource.id),
    getPrice(invoiceStreamId, resource.categoryId, userId),
  ]).then(([inForfait, { data: price }]) => ({
    inForfait,
    price: price || null,
    priceOptions: price ? [price] : [],
  }));

export const getAbsenceBalance = (resource, userId, date) => {
  if (!resource?.useCounter || !resource?.id) {
    return Promise.resolve({ absenceBalance: null });
  }
  return api.base
    .get(`${config.appUrls.api}/api/AbsenceCounter/Balance`, {
      params: {
        resourceId: resource.id,
        date: moment.utc(date).format('YYYY-MM-DD'),
        userId,
      },
    })
    .then(({ data }) => {
      const { balance, hoursInDay } = data || {};
      if (typeof balance === 'undefined' || balance === null || hoursInDay <= 0)
        return { absenceBalance: null };

      const balanceDays = balance / hoursInDay;
      return {
        absenceBalance: {
          ...data,
          balanceDays,
          maxDays: Math.max(0, balanceDays),
          maxMinutes: Math.max(0, balance * 60),
        },
        durationType: 1,
      };
    });
};

const fallbackStartTime = adminpulse.joinTime(9, 0);
export const getLastRegistrationTime = date => {
  if (!date) return Promise.resolve(fallbackStartTime);
  return api.base
    .get(
      `${config.appUrls.api}/api/registration/LastRegistrationTime?date=${moment(date).format(
        'YYYY-MM-DD',
      )}`,
    )
    .then(({ data }) => {
      if (!data) return fallbackStartTime;
      const momentDate = moment(data, 'HH:mm:ss');
      const h = momentDate.hour();
      const m = momentDate.minutes();
      return adminpulse.joinTime(h, m);
    });
};

export const getInteractionForRegistration = group => {
  if (!group)
    return Promise.resolve({
      interactionType: null,
      interactionTitle: null,
      interactionDescription: null,
    });
  return api.base
    .get(`${config.appUrls.interactions}/api/interactions/object/1/${group}`)
    .then(({ data }) => {
      const [interaction = null] = data || [];
      return interaction;
    });
};

export const emptyInteractionDescription =
  '<html>\n<head>\n\t<title></title>\n</head>\n<body style="height: auto; min-height: auto;"></body>\n</html>\n';

export const createOrUpdateInteraction = (
  {
    // Should be props on registration, so passing the registration to this functions should work
    interactionType = null,
    interactionDescription = null,
    interactionTitle = null,
    interactionId = null,
    relation = {},
    date,
    fromMinutes = 0,
    orgInteraction = null,
    userId,
  },
  group, // The second parameter should be the group id of the saved registration
) => {
  if (
    userId &&
    userId !== AuthService.getUserId() &&
    !AuthService.hasPermission({
      functionality: functionalities.InteractionsOthers,
      privilege: 'Update',
    })
  )
    if (
      userId &&
      userId === AuthService.getUserId() &&
      !AuthService.hasPermission({
        functionality: functionalities.InteractionsOwn,
        privilege: 'Update',
      })
    )
      return;

  const interactionChanged =
    (!orgInteraction &&
      ((interactionDescription && interactionDescription !== emptyInteractionDescription) ||
        interactionTitle)) ||
    (orgInteraction &&
      ((interactionDescription !== orgInteraction.description &&
        interactionDescription !== emptyInteractionDescription) ||
        interactionTitle !== orgInteraction.title ||
        interactionType !== orgInteraction.interactionType ||
        orgInteraction.relationId !== relation.id ||
        !moment(orgInteraction.date).isSame(date, 'day')));
  if (!interactionChanged) return Promise.resolve();

  // Create interaction date/time by combining registration date with start time
  const interactionDateTime = moment(date)
    .startOf('day')
    .add(fromMinutes || 0, 'minutes')
    .toDate();

  if (interactionId) {
    const updatedInteraction = {
      id: interactionId,
      description: interactionDescription,
      title: interactionTitle,
      interactionType,
      isPinned: orgInteraction.isPinned,
      date: interactionDateTime,
      relationId: relation.id,
    };
    return api.base.put(`${config.appUrls.interactions}/api/interactions`, updatedInteraction);
  }
  const newInteraction = {
    description: interactionDescription,
    title: interactionTitle,
    interactionType,
    date: interactionDateTime,
    relationId: relation.id,
    objectType: 1,
    objectId: group,
    interactionSource: 1,
  };
  return api.base.post(`${config.appUrls.interactions}/api/interactions/create`, newInteraction);
};

/** Uploads files as temporary documents (documentType 15), to be linked once saved. */
export const uploadTempAttachments = files => {
  const formData = new FormData();
  (files || []).forEach(file => formData.append('data', file));
  return api.base
    .post(`${config.appUrls.documents}/api/documents?documentType=15`, formData, {
      headers: { 'Content-Type': undefined },
    })
    .then(({ data }) => data || []);
};

export const deleteTempAttachment = documentId =>
  api.base.delete(`${config.appUrls.documents}/api/documents/${documentId}`);

/** Links uploaded temporary attachments to the saved interaction (and its relation). */
export const linkInteractionAttachments = (interactionId, relationId, attachments) => {
  const documentLinks = [];
  (attachments || []).forEach(attachment => {
    if (attachment.documentType !== 15) return; // Only link freshly uploaded temp documents
    documentLinks.push({
      documentId: attachment.id,
      linkId: interactionId,
      linkType: 6,
      recognizeTemporaryDocuments: true,
    });
    if (relationId) {
      documentLinks.push({ documentId: attachment.id, linkId: relationId, linkType: 3 });
    }
  });
  if (!documentLinks.length) return Promise.resolve([]);
  return api.base.post(`${config.appUrls.documents}/api/documents/linksmultiple`, documentLinks);
};
